import React from 'react';
import { Link } from 'react-router-dom';
import { MobileLayout } from '../../components/MobileLayout';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { usePlatform } from '../../context/PlatformContext';
import { MapPin, ClipboardList, FileText, Layers, ArrowRight, TrendingUp, Clock } from 'lucide-react';
// Trim "Milano · Navigli" -> "Navigli" for compact zone labels
function shortZone(zone: string): string {
  const parts = zone.split('·').map(s => s.trim());
  return parts[parts.length - 1] || zone;
}
export function CmDashboardPage() {
  const {
    jobs
  } = usePlatform();
  const daAssegnare = jobs.filter(j => j.status === 'da_assegnare');
  const oggi = jobs.filter(j => j.date === 'Oggi');
  // Read-only zone coverage overview (deep map lives in Opportunità di zona)
  const zoneCoverage = (() => {
    const m = new Map<string, number>();
    jobs.filter(j => j.status !== 'completata').forEach(j => m.set(j.zone, (m.get(j.zone) ?? 0) + 1));
    return Array.from(m.entries()).map(([zone, count]) => ({
      zone,
      count
    })).sort((a, b) => b.count - a.count).slice(0, 4);
  })();
  const maxZoneCount = zoneCoverage.reduce((mx, z) => Math.max(mx, z.count), 0);
  const liveStats = [{
    label: 'Opportunità di zona',
    value: '4',
    to: '/cm/opportunita'
  }, {
    label: 'Incarichi oggi',
    value: `${oggi.length}`,
    to: '/cm/incarichi'
  }, {
    label: 'Da assegnare',
    value: `${daAssegnare.length}`,
    to: '/cm/incarichi'
  }, {
    label: 'Cleaner attivi',
    value: '4',
    to: '/cm/team'
  }];
  const jobStatusLabel: Record<string, {
    label: string;
    cls: string;
  }> = {
    da_assegnare: {
      label: 'Da assegnare',
      cls: 'bg-amber-50 text-amber-700 border-amber-200'
    },
    assegnata: {
      label: 'Assegnata',
      cls: 'bg-muted text-muted-foreground border-border'
    },
    in_corso: {
      label: '● In corso',
      cls: 'bg-coral/5 text-coral border-coral/20'
    },
    completata: {
      label: 'Completata',
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    }
  };
  return <MobileLayout data-id="element-1736">
      {/* Header */}
      <div className="mb-6" data-id="element-1737">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-1738">
          Ciao, Cleaner Manager 👋
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-id="element-1739">
        {liveStats.map(s => <Link key={s.label} to={s.to} className="bg-white border border-border rounded-xl p-4 hover:border-coral/50 hover:shadow-sm transition-all" data-id="element-1740">
            <div className="text-2xl font-bold text-foreground" data-id="element-1741">{s.value}</div>
            <div className="text-xs text-muted-foreground mt-1" data-id="element-1742">{s.label}</div>
          </Link>)}
      </div>

      {/* Today jobs */}
      <Card className="mb-6" data-id="element-1743">
        <CardHeader icon={<ClipboardList size={15} className="text-muted-foreground" data-id="element-1745" />} title="Incarichi di oggi" action={<Link to="/cm/incarichi" className="text-xs font-medium text-coral hover:underline" data-id="element-1746">
              Vedi tutti
            </Link>} data-id="element-1744" />
        <CardContent data-id="element-1747">
          {oggi.length === 0 && <p className="text-sm text-muted-foreground text-center py-4" data-id="element-1748">
              Nessun incarico per oggi.
            </p>}
          <ul className="space-y-3" data-id="element-1749">
            {oggi.map(j => <li key={j.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-1750">
                <div className="min-w-0" data-id="element-1751">
                  <div className="text-sm font-semibold text-foreground truncate" data-id="element-1752">
                    {j.apartmentName}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5" data-id="element-1753">
                    <Clock size={12} data-id="element-1754" />
                    {j.time}
                    <span data-id="element-1755">·</span>
                    {j.cleanerName ?? 'Da assegnare'}
                  </div>
                </div>
                <span className={`text-[10px] font-medium px-2 py-1 rounded-full border flex-shrink-0 ${jobStatusLabel[j.status].cls}`} data-id="element-1756">
                  {jobStatusLabel[j.status].label}
                </span>
              </li>)}
          </ul>
        </CardContent>
      </Card>

      {/* Zone coverage overview (read-only — deep map in Opportunità di zona) */}
      {zoneCoverage.length > 0 && <Card className="mb-6" data-id="element-1757">
          <CardHeader icon={<MapPin size={15} className="text-muted-foreground" data-id="element-1759" />} title="Copertura zone" action={<Link to="/cm/opportunita" className="text-xs font-medium text-coral hover:underline inline-flex items-center gap-1" data-id="element-1760">
                Mappa zone
                <ArrowRight size={13} data-id="element-1761" />
              </Link>} data-id="element-1758" />
          <CardContent data-id="element-1762">
            <ul className="space-y-3" data-id="element-1763">
              {zoneCoverage.map((z, i) => {
            const pct = maxZoneCount > 0 ? z.count / maxZoneCount * 100 : 0;
            const isTop = z.count === maxZoneCount && maxZoneCount > 0;
            return <li key={z.zone} data-id="element-1764">
                    <div className="flex items-center justify-between gap-3 mb-1.5" data-id="element-1765">
                      <span className="flex items-center gap-2 min-w-0" data-id="element-1766">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isTop ? 'bg-coral' : 'bg-navy'}`} data-id="element-1767" />
                        <span className="text-sm font-medium text-foreground truncate" data-id="element-1768">
                          {shortZone(z.zone)}
                        </span>
                      </span>
                      <span className="text-xs font-semibold text-navy flex-shrink-0" data-id="element-1769">
                        {z.count}{' '}
                        <span className="font-normal text-muted-foreground" data-id="element-1770">
                          {z.count === 1 ? 'intervento' : 'interventi'}
                        </span>
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-1771">
                      <div className={`h-1.5 rounded-full transition-all ${isTop ? 'bg-coral' : 'bg-navy'}`} style={{
                  width: `${pct}%`
                }} data-id="element-1772" />
                    </div>
                  </li>;
          })}
            </ul>
          </CardContent>
        </Card>}

      {/* Earnings teaser */}
      <Link to="/cm/guadagni" className="block bg-white border border-border rounded-xl p-5 hover:border-coral/50 hover:shadow-sm transition-all" data-id="element-1773">
        <div className="flex items-center justify-between" data-id="element-1774">
          <div className="flex items-center gap-3" data-id="element-1775">
            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center" data-id="element-1776">
              <TrendingUp size={20} className="text-navy" data-id="element-1777" />
            </div>
            <div data-id="element-1778">
              <div className="text-sm font-semibold text-foreground" data-id="element-1779">
                Guadagni stimati questo mese
              </div>
              <div className="text-xs text-muted-foreground" data-id="element-1780">
                Su contratti e interventi confermati
              </div>
            </div>
          </div>
          <div className="text-lg font-bold text-coral" data-id="element-1781">€4.280</div>
        </div>
      </Link>
    </MobileLayout>;
}